import { gettext } from '@eventbrite/i18n';
import { CalendarCheckmarkIcon } from '@eventbrite/marmalade';
import loadable from '@loadable/component';
import classNames from 'classnames';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import type {
    ChildEventProps,
    DateAndTimeScopedProps,
    Event,
    LocalizedDateTime,
} from '../../../../types';
import { SALES_STATUS } from '../../../../types';
import {
    Detail,
    DetailContent,
    DetailInner,
} from '../../../_shared/Detail/Detail';

import {
    getAttendeeTimezone,
    shouldShowDateInAttendeeTimezone,
} from '@eventbrite/event-date-time';
import { eventIsOnSale, eventIsSoldOut } from '../../../../utils';
import {
    EventDetailsSection,
    EventDetailsSectionTitle,
} from '../../EventDetails';
import { ChildEventsSlider } from '../ChildEventsSelection/ChildEventsSlider';
import { shouldShowMoreOptions } from './utils';

import { useEventBasicInformationContext } from '../../../../contexts';
import { MoreOptionsButton } from '../ChildEventsSelection/MoreOptionsButton';
import { DisplayDate } from './DisplayDate';

import './DateAndTime.scss';

export type DateAndTimeProps = Pick<
    DateAndTimeScopedProps,
    'doorTime' | 'shouldShowDoorTime' | 'displayDate'
> & {
    locale: string;
    event: Pick<
        Event,
        | 'start'
        | 'end'
        | 'hideStartDate'
        | 'hideEndDate'
        | 'childEvents'
        | 'isOnlineEvent'
    > & {
        listingDisplayFlags: {
            shouldShowTimezone: boolean;
        };
    };
};

const getDateInAttendeeTimezone = (
    date: LocalizedDateTime,
    userTimezone: string | null,
) => {
    const formattedDate = moment
        .tz(date.utc, userTimezone)
        .format('YYYY-MM-DDTHH:mm:ss');

    return {
        ...date,
        local: formattedDate,
        timezone: userTimezone as string,
    };
};

const getChildEventsInAttendeeTimeZone = (
    childEvents: ChildEventProps[],
    userTimezone: string | null,
): Array<ChildEventProps> => {
    const childEventsInAttendeeTimeZone: Array<ChildEventProps> = [];

    childEvents?.forEach((childEvent) => {
        const childEventInAttendeeTimeZone: ChildEventProps = {
            ...childEvent,
            start: getDateInAttendeeTimezone(childEvent.start, userTimezone),
            end: getDateInAttendeeTimezone(childEvent.end, userTimezone),
        };
        childEventsInAttendeeTimeZone.push(childEventInAttendeeTimeZone);
    });

    return childEventsInAttendeeTimeZone;
};

const LABEL_ID = 'date-time-heading';

const filterOnSaleEventDates = (childEvents: Array<ChildEventProps>) => {
    return childEvents.filter(
        (event) =>
            eventIsOnSale(event) || event?.sales_status == SALES_STATUS.DEFAULT,
    );
};

export const DateAndTime = ({
    event,
    displayDate,
    shouldShowDoorTime,
    doorTime,
    locale,
}: DateAndTimeProps) => {
    const [moreOptionsModalOpened, setMoreOptionsModalOpened] = useState(false);
    const basicEventInformation = useEventBasicInformationContext();

    const { childEvents, isOnlineEvent } = event;
    let eventsToDisplayOnSlider = childEvents
        ? childEvents.filter(
              (event) =>
                  eventIsSoldOut(event) ||
                  eventIsOnSale(event) ||
                  event?.sales_status == SALES_STATUS.DEFAULT,
          )
        : [];
    if (!displayDate) {
        return null;
    }

    const handleMoreOptionsClick = () => {
        setMoreOptionsModalOpened(true);
    };

    // Can we move this to its own component?
    const LazyMoreOptionsModal = loadable(
        () =>
            import('../ChildEventsSelection/MoreOptionsModal/MoreOptionsModal'),
    );

    const selectedEventData = {
        ...event,
        id: basicEventInformation.id,
        start: basicEventInformation.start || event.start,
        end: basicEventInformation.end || event.end,
    };

    if (isOnlineEvent) {
        const userTimezone = getAttendeeTimezone();
        const shouldShowChildEventsInAttendeeTimeZone =
            shouldShowDateInAttendeeTimezone({ isOnlineEvent, userTimezone });

        if (shouldShowChildEventsInAttendeeTimeZone) {
            eventsToDisplayOnSlider = getChildEventsInAttendeeTimeZone(
                eventsToDisplayOnSlider,
                userTimezone,
            );

            selectedEventData.start = getDateInAttendeeTimezone(
                selectedEventData.start,
                userTimezone,
            );
            selectedEventData.end = getDateInAttendeeTimezone(
                selectedEventData.end,
                userTimezone,
            );
        }
    }

    const shouldShowChildEvents = eventsToDisplayOnSlider.length > 0;
    const onSaleEvents = filterOnSaleEventDates(childEvents ?? []);

    return (
        <>
            <EventDetailsSection aria-labelledby={LABEL_ID}>
                <Detail>
                    <DetailInner>
                        <DetailContent>
                            <EventDetailsSectionTitle>
                                {shouldShowChildEvents ? (
                                    <h2>{gettext('Select date and time')}</h2>
                                ) : (
                                    <h2>{gettext('Date and time')}</h2>
                                )}
                            </EventDetailsSectionTitle>
                            <div
                                className={classNames({
                                    'select-date-and-time':
                                        shouldShowChildEvents,
                                })}
                            >
                                <div className="date-info">
                                    <CalendarCheckmarkIcon className="date-info__icon" />
                                    <div data-testid="display-date-container">
                                        <DisplayDate
                                            doorTime={
                                                shouldShowDoorTime
                                                    ? doorTime
                                                    : null
                                            }
                                            event={{
                                                ...selectedEventData,
                                                isOnlineEvent,
                                            }}
                                            locale={locale}
                                        />
                                        {eventsToDisplayOnSlider &&
                                            shouldShowMoreOptions(
                                                eventsToDisplayOnSlider,
                                            ) && (
                                                <MoreOptionsButton
                                                    onButtonClick={
                                                        handleMoreOptionsClick
                                                    }
                                                />
                                            )}
                                    </div>
                                </div>
                                {shouldShowChildEvents && (
                                    <ChildEventsSlider
                                        childEvents={eventsToDisplayOnSlider}
                                        shouldShowMoreOptions={
                                            eventsToDisplayOnSlider &&
                                            shouldShowMoreOptions(
                                                eventsToDisplayOnSlider,
                                            )
                                        }
                                        currentEventId={selectedEventData.id}
                                        onMoreOptionsClick={
                                            handleMoreOptionsClick
                                        }
                                    />
                                )}
                            </div>
                        </DetailContent>
                    </DetailInner>
                </Detail>
                {moreOptionsModalOpened && (
                    <LazyMoreOptionsModal
                        onClose={() => setMoreOptionsModalOpened(false)}
                        opened={moreOptionsModalOpened}
                        childEvents={onSaleEvents}
                        currentEventStartDate={selectedEventData.start}
                        currentEventId={selectedEventData.id}
                    />
                )}
            </EventDetailsSection>
        </>
    );
};
